import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action, AdditionalCriteriaType } from 'app/pages/DiscountEditor/actionTypes';
import { Modal, Select, Button, DatePicker, Alert, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { setAddOrEditCriteriaModal, saveStudentStartDateCriteria } from 'app/pages/DiscountEditor/actions';
import { getStudyStartDateCriteria, getAddOrEditModal } from 'app/pages/DiscountEditor/modules/AdditionalCriteria';
import { SavedStudyStartDateCriteria } from 'app/pages/DiscountEditor/modules/AdditionalCriteria/state';
import { UnitsFilterType } from 'app/shared/model/ProductUnitFilter';
import DateHelper from 'app/helpers/dateHelper';

const { Text } = Typography;

const parseTypedDate = (value: string): Dayjs | null => {
    const trimmed = value.replace(/\s/g, '');
    if (trimmed === '') return null;
    const parsed = dayjs(trimmed, DateHelper.DATE_PICKER_FORMATS, true);
    return parsed.isValid() ? parsed : null;
};

interface StateProps {
    isOpen: boolean,
    unitsType: UnitsFilterType, 
    minDate?: string
    maxDate?: string
}

interface DispatchProps { 
    onClose: () => void, 
    onSave: (criteria: SavedStudyStartDateCriteria) => void
}

type InternalProps = StateProps & DispatchProps;

interface InternalState {
    unitsType: UnitsFilterType
    minDate: string
    maxDate: string
    formStatus: FormStatus 
}

interface FormStatus {
    isValid: boolean, 
    errorMessage: string|undefined
}

class StudyStartDateCriteriaModal extends React.Component<InternalProps, InternalState> {
    constructor(props: InternalProps) {
        super(props);
        this.state = {
            unitsType: this.props.unitsType, 
            minDate: this.props.minDate || "", 
            maxDate: this.props.maxDate || "", 
            formStatus: {
                isValid: true, 
                errorMessage: undefined
            }
        }; 
    }

    private StartDateOptions = [
        {label: 'on or before', value: UnitsFilterType.MaxUnits, key: 1},
        {label: 'on or after', value: UnitsFilterType.MinUnits, key: 2},
        {label: 'between', value: UnitsFilterType.BetweenUnits, key: 3},
    ]

    private save() {
        let isValid = true; 
        let errorMessage = undefined; 

        if (this.state.unitsType === UnitsFilterType.MaxUnits && this.state.maxDate === "") {
            isValid = false;
            errorMessage = "Please enter the 'on or after' date"; 
        }
        else if (this.state.unitsType === UnitsFilterType.MinUnits && this.state.minDate === "") {
            isValid = false;
            errorMessage = "Please enter the 'on or before' date"; 
        }
        else if (this.state.unitsType === UnitsFilterType.BetweenUnits) 
        {
            if (this.state.minDate === "" || this.state.maxDate === ""){
                isValid = false;
                errorMessage = "Please enter the date range the student must start between"; 
            }
            else if (dayjs(this.state.minDate).isAfter(dayjs(this.state.maxDate))) {
                isValid = false;
                errorMessage = "The between date range is incorrect. The date from must be before the date to"; 
            }            
        }        
       
        this.setState({
            formStatus: {
                isValid: isValid, 
                errorMessage: errorMessage
            }
        }); 

        if (!isValid) {
            return; 
        }

        this.props.onSave({
            hasCriteria: true, 
            unitsType: this.state.unitsType, 
            minDate: this.state.minDate !== "" ? this.state.minDate : undefined,
            maxDate: this.state.maxDate !== "" ? this.state.maxDate : undefined
        }); 
    }    

    private applyTypedDate(field: 'minDate'|'maxDate', rawValue: string) {
        const parsed = parseTypedDate(rawValue);
        if (!parsed) {
            return;
        }
        this.setState({ [field]: parsed.format('YYYY-MM-DD') } as Pick<InternalState, 'minDate'|'maxDate'>);
    }

    private typedDateHandlers(field: 'minDate'|'maxDate') {
        return {
            onBlur: (e: React.FocusEvent<HTMLElement>) =>
                this.applyTypedDate(field, (e.target as HTMLInputElement)?.value ?? ''),
            onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key !== 'Enter') return;
                const value = (e.target as HTMLInputElement)?.value ?? '';
                if (!parseTypedDate(value)) return;
                e.preventDefault();
                this.applyTypedDate(field, value);
            }
        };
    }

    private showMinUnits() {
        return this.state.unitsType === UnitsFilterType.BetweenUnits ||
               this.state.unitsType === UnitsFilterType.MinUnits;
    }
    
    private showMaxUnits() {
        return this.state.unitsType === UnitsFilterType.BetweenUnits ||
               this.state.unitsType === UnitsFilterType.MaxUnits;
    }
    

    render() {
        return <Modal 
                open={this.props.isOpen} 
                onCancel={this.props.onClose}
                title={<Text strong style={{ fontSize: 16, color: '#262626' }}>Study start date</Text>}
                footer={[
                    <Button key="cancel" onClick={this.props.onClose}>Cancel</Button>,
                    <Button 
                        key="save" 
                        type="primary" 
                        icon={<CheckOutlined />} 
                        onClick={e => this.save()}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Save
                    </Button>
                ]}
                width={600}
            >
            {!this.state.formStatus.isValid && (
                <Alert
                    type="error"
                    title={this.state.formStatus.errorMessage}
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, lineHeight: '32px' }}>
                <Text>The student must start studying</Text>
                <Select 
                    key="units-filter"
                    style={{ width: 150 }}
                    options={this.StartDateOptions} 
                    value={this.state.unitsType}
                    onChange={(value) => this.setState({unitsType: value as UnitsFilterType})} 
                />
                {this.showMinUnits() && (
                    <>
                        <Text type="danger" style={{ lineHeight: '32px' }}>*</Text>
                        <DatePicker
                            value={this.state.minDate ? dayjs(this.state.minDate) : null}
                            onChange={(date: Dayjs | null) => {
                                this.setState({minDate: date ? date.format('YYYY-MM-DD') : ''});
                            }}
                            {...this.typedDateHandlers('minDate')}
                            format={DateHelper.DATE_PICKER_FORMATS}
                            style={{ width: 170 }}
                            placeholder="Type or select"
                        />
                    </>
                )}
                {this.state.unitsType === UnitsFilterType.BetweenUnits && this.showMinUnits() && (
                    <Text>and</Text>
                )}
                {this.showMaxUnits() && (
                    <>
                        {!this.showMinUnits() && (
                            <Text type="danger" style={{ lineHeight: '32px' }}>*</Text>
                        )}
                        <DatePicker
                            value={this.state.maxDate ? dayjs(this.state.maxDate) : null}
                            onChange={(date: Dayjs | null) => {
                                this.setState({maxDate: date ? date.format('YYYY-MM-DD') : ''});
                            }}
                            {...this.typedDateHandlers('maxDate')}
                            format={DateHelper.DATE_PICKER_FORMATS}
                            style={{ width: 170 }}
                            placeholder="Type or select"
                        />
                    </>
                )}
            </div>
        </Modal>
    }
}

const mapStateToProps = (state:any, ownProps: {}) : StateProps => {
    let criteria = getStudyStartDateCriteria(state); 
    const currentModal = getAddOrEditModal(state);

    if (criteria.hasCriteria === false){
        criteria = {
            hasCriteria: true,
            unitsType: UnitsFilterType.MaxUnits, 
            minDate: undefined,
            maxDate: undefined
        }
    }

    return {
        isOpen: currentModal == AdditionalCriteriaType.StudyStartDate, 
        unitsType: criteria.unitsType, 
        minDate: criteria.minDate,
        maxDate: criteria.maxDate
    }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, Action>) : DispatchProps =>  {
    return {
      onClose: () => dispatch(setAddOrEditCriteriaModal(AdditionalCriteriaType.None)),
      onSave: (criteria: SavedStudyStartDateCriteria) => dispatch(saveStudentStartDateCriteria(criteria))
    }; 
  }

export default connect(mapStateToProps, mapDispatchToProps)(StudyStartDateCriteriaModal);
